import { useEffect, useMemo, useState } from "react";
import {
	useList as useListRaw,
	useListKeys as useListKeysRaw,
	useListVals as useListValsRaw,
	useObject as useObjectRaw,
	useObjectVal as useObjectValRaw,
} from "react-firebase-hooks/database";
import { useAuth } from "../AuthContext/AuthContext";
import { getCallable } from "./functionsClient";

const CACHE_TTL_MS = 20 * 1000;
const demoCollectionCache = new Map();
const demoValueCache = new Map();
const inflightCollectionRequests = new Map();
const inflightValueRequests = new Map();
const demoPathVersions = new Map();
const demoCacheListeners = new Set();

function extractPathFromQuery(query) {
	if (!query) {
		return null;
	}

	const refLike = query.ref || query;
	if (!refLike || typeof refLike.toString !== "function") {
		return null;
	}

	let pathCandidate = refLike.toString();
	if (!pathCandidate) {
		return null;
	}

	try {
		const parsedUrl = new URL(pathCandidate);
		pathCandidate = parsedUrl.pathname || "";
	} catch (_error) {
		pathCandidate = pathCandidate.split("?")[0];
	}

	pathCandidate = pathCandidate
		.replace(/\/+/g, "/")
		.replace(/^\/+|\/+$/g, "")
		.replace(/\.json$/, "");

	return pathCandidate || null;
}

function normalizePath(path) {
	if (!path) {
		return null;
	}

	let pathCandidate = String(path);

	try {
		const parsedUrl = new URL(pathCandidate);
		pathCandidate = parsedUrl.pathname || "";
	} catch (_error) {
		pathCandidate = pathCandidate.split("?")[0];
	}

	pathCandidate = pathCandidate
		.replace(/\/+/g, "/")
		.replace(/^\/+|\/+$/g, "")
		.replace(/\.json$/, "");

	return pathCandidate || null;
}

function isDemoPath(path) {
	return typeof path === "string" && path.startsWith("talmidsdb/");
}

function getAffectedDemoPaths(path) {
	const normalizedPath = normalizePath(path);
	if (!isDemoPath(normalizedPath)) {
		return [];
	}

	const sections = normalizedPath.split("/").filter(Boolean);
	const affectedPaths = [];
	for (let index = sections.length; index > 0; index -= 1) {
		affectedPaths.push(sections.slice(0, index).join("/"));
	}

	return affectedPaths;
}

function getDemoPathVersion(path) {
	return demoPathVersions.get(path) || 0;
}

function bumpDemoPathVersion(path) {
	demoPathVersions.set(path, getDemoPathVersion(path) + 1);
}

function notifyDemoCacheListeners() {
	demoCacheListeners.forEach((listener) => {
		listener();
	});
}

function subscribeToDemoCache(listener) {
	demoCacheListeners.add(listener);
	return () => {
		demoCacheListeners.delete(listener);
	};
}

export function invalidateDemoData(paths) {
	const pathList = Array.isArray(paths) ? paths : [paths];
	const affectedPaths = new Set();

	pathList.forEach((path) => {
		getAffectedDemoPaths(path).forEach((affectedPath) => {
			affectedPaths.add(affectedPath);
		});
	});

	if (!affectedPaths.size) {
		return;
	}

	affectedPaths.forEach((path) => {
		demoCollectionCache.delete(path);
		demoValueCache.delete(path);
		inflightCollectionRequests.delete(path);
		inflightValueRequests.delete(path);
		bumpDemoPathVersion(path);
	});

	notifyDemoCacheListeners();
}

function getPathLeaf(path) {
	if (!path) {
		return null;
	}
	const sections = String(path).split("/").filter(Boolean);
	return sections.length ? sections[sections.length - 1] : null;
}

function getValueAtPath(rawValue, nestedPath) {
	if (!nestedPath) {
		return rawValue;
	}

	const sections = String(nestedPath).split("/").filter(Boolean);
	let current = rawValue;
	for (const section of sections) {
		if (!current || typeof current !== "object") {
			return undefined;
		}
		current = current[section];
	}
	return current;
}

function toSnapshot(rawValue, key = null) {
	return {
		key,
		exists: () => rawValue !== null && typeof rawValue !== "undefined",
		val: () => rawValue,
		toJSON: () => rawValue,
		hasChild: (childPath) => {
			const childValue = getValueAtPath(rawValue, childPath);
			return childValue !== null && typeof childValue !== "undefined";
		},
		child: (childPath) => {
			const childValue = getValueAtPath(rawValue, childPath);
			return toSnapshot(childValue, getPathLeaf(childPath));
		},
		forEach: (callback) => {
			if (!rawValue || typeof rawValue !== "object" || Array.isArray(rawValue)) {
				return false;
			}

			const keys = Object.keys(rawValue);
			for (const childKey of keys) {
				const childSnap = toSnapshot(rawValue[childKey], childKey);
				if (callback(childSnap) === true) {
					return true;
				}
			}
			return false;
		},
	};
}

function normalizeListWithOptions(listValue, options) {
	let normalized = Array.isArray(listValue) ? listValue : [];

	if (typeof options?.keyField === "string" && options.keyField.trim()) {
		const keyField = options.keyField.trim();
		normalized = normalized.map((item, index) => {
			if (item && typeof item === "object" && !Array.isArray(item)) {
				return {
					...item,
					[keyField]: item[keyField] ?? item.id ?? String(index),
				};
			}
			return {
				[keyField]: String(index),
				value: item,
			};
		});
	}

	if (typeof options?.transform === "function") {
		normalized = normalized.map((item) => options.transform(item));
	}

	return normalized;
}

function normalizeObjectValWithOptions(value, options, path) {
	let normalized = value;

	if (
		typeof options?.keyField === "string" &&
		options.keyField.trim() &&
		normalized &&
		typeof normalized === "object" &&
		!Array.isArray(normalized)
	) {
		const keyField = options.keyField.trim();
		normalized = {
			...normalized,
			[keyField]: normalized[keyField] ?? getPathLeaf(path),
		};
	}

	if (typeof options?.transform === "function") {
		normalized = options.transform(normalized);
	}

	return normalized;
}

async function fetchDemoCollection(path) {
	const normalizedPath = String(path || "").trim();
	const now = Date.now();
	const cached = demoCollectionCache.get(normalizedPath);

	if (cached && now - cached.timestamp < CACHE_TTL_MS) {
		return cached.data;
	}

	const currentInflight = inflightCollectionRequests.get(normalizedPath);
	if (currentInflight) {
		return currentInflight;
	}

	const requestPromise = getCallable("listCollections")({ paths: [normalizedPath] })
		.then((res) => {
			const data = Array.isArray(res?.data?.collections?.[normalizedPath]) ?
				res.data.collections[normalizedPath] :
				[];

			demoCollectionCache.set(normalizedPath, {
				data,
				timestamp: Date.now(),
			});
			return data;
		})
		.finally(() => {
			inflightCollectionRequests.delete(normalizedPath);
		});

	inflightCollectionRequests.set(normalizedPath, requestPromise);
	return requestPromise;
}

async function fetchDemoValue(path) {
	const normalizedPath = String(path || "").trim();
	const now = Date.now();
	const cached = demoValueCache.get(normalizedPath);

	if (cached && now - cached.timestamp < CACHE_TTL_MS) {
		return cached.data;
	}

	const currentInflight = inflightValueRequests.get(normalizedPath);
	if (currentInflight) {
		return currentInflight;
	}

	const requestPromise = getCallable("readPaths")({ paths: [normalizedPath] })
		.then((res) => {
			const rawValue = res?.data?.values?.[normalizedPath];
			demoValueCache.set(normalizedPath, {
				data: rawValue,
				timestamp: Date.now(),
			});
			return rawValue;
		})
		.finally(() => {
			inflightValueRequests.delete(normalizedPath);
		});

	inflightValueRequests.set(normalizedPath, requestPromise);
	return requestPromise;
}

function useSecureDemoData(path, fetcher) {
	const { firebaseUser, loading: authLoading } = useAuth();
	const [value, setValue] = useState(undefined);
	const [loading, setLoading] = useState(Boolean(path));
	const [error, setError] = useState(undefined);
	const [cacheVersion, setCacheVersion] = useState(() => getDemoPathVersion(path));

	useEffect(() => {
		setCacheVersion(getDemoPathVersion(path));

		if (!path) {
			return undefined;
		}

		return subscribeToDemoCache(() => {
			setCacheVersion(getDemoPathVersion(path));
		});
	}, [path]);

	useEffect(() => {
		let isMounted = true;

		if (!path) {
			setValue(undefined);
			setLoading(false);
			setError(undefined);
			return () => {
				isMounted = false;
			};
		}

		if (authLoading) {
			setLoading(true);
			return () => {
				isMounted = false;
			};
		}

		if (!firebaseUser) {
			setValue(undefined);
			setLoading(false);
			setError(new Error("Not authenticated"));
			return () => {
				isMounted = false;
			};
		}

		setLoading(true);
		setError(undefined);

		const executeSecureFetch = async () => {
			try {
				// Prime the auth token before callable invocation.
				await firebaseUser.getIdToken();
				return await fetcher(path);
			} catch (firstError) {
				const errorCode = String(firstError?.code || firstError?.message || "");
				if (errorCode.includes("unauthenticated")) {
					// Refresh token once and retry to handle race/stale token cases.
					await firebaseUser.getIdToken(true);
					return await fetcher(path);
				}
				throw firstError;
			}
		};

		executeSecureFetch()
			.then((data) => {
				if (!isMounted) return;
				setValue(data);
				setLoading(false);
			})
			.catch((err) => {
				if (!isMounted) return;
				setValue(undefined);
				setError(err);
				setLoading(false);
			});

		return () => {
			isMounted = false;
		};
	}, [path, authLoading, firebaseUser?.uid, fetcher, cacheVersion]);

	return [value, loading, error];
}

function useSecureDemoListVals(path, options) {
	const [value, loading, error] = useSecureDemoData(path, fetchDemoCollection);
	const normalizedValue = useMemo(
		() => normalizeListWithOptions(value, options),
		[value, options]
	);
	return [normalizedValue, loading, error];
}

function useSecureDemoObjectVal(path, options) {
	const [value, loading, error] = useSecureDemoData(path, fetchDemoValue);
	const normalizedValue = useMemo(
		() => normalizeObjectValWithOptions(value, options, path),
		[value, options, path]
	);
	return [normalizedValue, loading, error];
}

export function useListVals(query, options) {
	const path = useMemo(() => extractPathFromQuery(query), [query]);
	const demoPath = isDemoPath(path) ? path : null;

	const secureResult = useSecureDemoListVals(demoPath, options);
	const rawResult = useListValsRaw(demoPath ? null : query, options);

	return demoPath ? secureResult : rawResult;
}

export function useList(query, options) {
	const path = useMemo(() => extractPathFromQuery(query), [query]);
	const demoPath = isDemoPath(path) ? path : null;

	const [listValues, loading, error] = useSecureDemoListVals(demoPath, options);
	const secureSnapshots = useMemo(() => {
		if (!Array.isArray(listValues)) {
			return [];
		}

		return listValues.map((item, index) => {
			const key = item?.id != null ? String(item.id) : String(index);
			return toSnapshot(item, key);
		});
	}, [listValues]);

	const rawResult = useListRaw(demoPath ? null : query, options);
	return demoPath ? [secureSnapshots, loading, error] : rawResult;
}

export function useListKeys(query, options) {
	const path = useMemo(() => extractPathFromQuery(query), [query]);
	const demoPath = isDemoPath(path) ? path : null;

	const [listValues, loading, error] = useSecureDemoListVals(demoPath, options);
	const secureKeys = useMemo(() => {
		if (!Array.isArray(listValues)) {
			return [];
		}

		return listValues.map((item, index) => {
			return item?.id != null ? String(item.id) : String(index);
		});
	}, [listValues]);

	const rawResult = useListKeysRaw(demoPath ? null : query, options);
	return demoPath ? [secureKeys, loading, error] : rawResult;
}

export function useObject(query, options) {
	const path = useMemo(() => extractPathFromQuery(query), [query]);
	const demoPath = isDemoPath(path) ? path : null;
	const [value, loading, error] = useSecureDemoData(demoPath, fetchDemoValue);

	const secureSnapshot = useMemo(() => {
		if (!demoPath) {
			return undefined;
		}

		if (loading && typeof value === "undefined") {
			return undefined;
		}

		return toSnapshot(value, getPathLeaf(demoPath));
	}, [demoPath, loading, value]);

	const rawResult = useObjectRaw(demoPath ? null : query, options);
	return demoPath ? [secureSnapshot, loading, error] : rawResult;
}

export function useObjectVal(query, options) {
	const path = useMemo(() => extractPathFromQuery(query), [query]);
	const demoPath = isDemoPath(path) ? path : null;

	const secureResult = useSecureDemoObjectVal(demoPath, options);
	const rawResult = useObjectValRaw(demoPath ? null : query, options);

	return demoPath ? secureResult : rawResult;
}
